import PropTypes from 'prop-types'

import { EMPTY_STEP_METRICS, formatCount } from './campaignFlowMock'
import styles from './OtaBuster.module.scss'

function EmailBadgeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M1.5 3.25A1.25 1.25 0 012.75 2h6.5a1.25 1.25 0 011.25 1.25v5.5A1.25 1.25 0 019.25 10H2.75A1.25 1.25 0 011.5 8.75v-5.5z"
        stroke="currentColor"
        strokeWidth="1.1"
      />
      <path d="M2 3.75l4 2.75 4-2.75" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  )
}

function PreviewIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M1.5 7s2.25-3.75 5.5-3.75S12.5 7 12.5 7s-2.25 3.75-5.5 3.75S1.5 7 1.5 7z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <circle cx="7" cy="7" r="1.75" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  )
}

function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M5 3.5v7M9 3.5v7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function EditIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M7.5 2.5l2 2-5 5H2.5V7.5l5-5z"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function StepTypeIcon({ color }) {
  return (
    <span className={styles.stepTypeIcon} style={{ background: `${color}24`, color }}>
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <path
          d="M3.5 4.75c0-1.105.895-2 2-2h7c1.105 0 2 .895 2 2v5.5c0 1.105-.895 2-2 2H8.2l-2.8 2.4a.75.75 0 01-1.22-.58V12.25h-1.7c-1.105 0-2-.895-2-2V4.75z"
          fill="currentColor"
        />
      </svg>
    </span>
  )
}

StepTypeIcon.propTypes = {
  color: PropTypes.string.isRequired
}

function OfferBadge({ label, highlighted }) {
  const className = highlighted ? styles.offerBadgeHighlighted : styles.offerBadgeMuted
  return <span className={className}>{label}</span>
}

OfferBadge.propTypes = {
  label: PropTypes.string.isRequired,
  highlighted: PropTypes.bool
}

OfferBadge.defaultProps = {
  highlighted: false
}

function CampaignStepCard({
  step,
  metrics,
  preview,
  isActive,
  hasEmailCampaign,
  hasDraftChanges,
  onEdit,
  onPause,
  onEnable,
  onPreviewSavibook
}) {
  const cardClassNames = [styles.stepCard, !isActive && styles.stepCardInactive]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={cardClassNames} data-testid={`ota-buster-step-${step.id}`}>
      <div className={styles.stepCardBody}>
        <span className={styles.stepNumberBadge}>{step.stepNumber}</span>

        <div className={styles.stepContentColumn}>
          <div className={styles.stepHeaderRow}>
            <StepTypeIcon color={step.iconColor} />
            <div className={styles.stepHeadingGroup}>
              <div className={styles.stepTitleRow}>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <span className={styles.stepTitleSep} aria-hidden="true">
                  ·
                </span>
                <span className={styles.channelBadgeInline}>
                  <EmailBadgeIcon />
                  Email + Text
                </span>
                <OfferBadge
                  label={step.offerLabel}
                  highlighted={Boolean(step.hasRoomUpgradeOffer)}
                />
                {hasDraftChanges && (
                  <span className={styles.draftBadge}>Draft changes</span>
                )}
              </div>
              {!hasEmailCampaign && (
                <span className={styles.emailNotConfigured}>Email not configured</span>
              )}
              <p className={styles.stepPreview}>{preview}</p>
            </div>
          </div>

          <div className={styles.stepActions}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => onPreviewSavibook(step)}
              data-testid={`ota-buster-preview-${step.id}`}
            >
              <PreviewIcon />
              Preview
            </button>
            {isActive ? (
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => onPause?.(step)}
                data-testid={`ota-buster-pause-${step.id}`}
              >
                <PauseIcon />
                Pause
              </button>
            ) : (
              <button
                type="button"
                className={styles.actionButtonPrimary}
                onClick={() => onEnable?.(step)}
                data-testid={`ota-buster-enable-${step.id}`}
              >
                Enable step
              </button>
            )}
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => onEdit?.()}
              data-testid={`ota-buster-edit-${step.id}`}
            >
              <EditIcon />
              Edit stage campaign
            </button>
          </div>
        </div>

        {isActive ? (
          <div className={styles.stepStatsPanel}>
            <div className={styles.statBlock}>
              <span className={styles.statLabel}>Guests reached</span>
              <span className={styles.statValue}>{formatCount(metrics.guests)}</span>
            </div>
            <div className={styles.statBlockGroup}>
              <div className={styles.statBlock}>
                <span className={styles.statLabel}>Click through rate</span>
                <span className={styles.statValue}>{metrics.clickThroughRate}%</span>
              </div>
              <div className={styles.statBlock}>
                <span className={styles.statLabel}>Engagement</span>
                <span className={styles.statValue}>{metrics.engagementRate}%</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.inactivePanel}>
            <div className={styles.inactiveTitle}>This step is inactive</div>
            <div className={styles.inactiveDescription}>
              Enable to keep sending this email in the OTA guest journey. Other steps stay
              as they are.
            </div>
          </div>
        )}
      </div>

      <div className={styles.guestDataRow}>
        <span className={styles.guestDataLabel}>Guest data captured</span>
        <span className={styles.guestDataValue}>
          {formatCount(metrics.identityCaptures)}
          <span className={styles.guestDataTotal}>total</span>
        </span>
        <button type="button" className={styles.guestDataDetail} disabled>
          See detail
        </button>
      </div>
    </article>
  )
}

CampaignStepCard.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    iconColor: PropTypes.string.isRequired,
    stepNumber: PropTypes.number.isRequired,
    offerLabel: PropTypes.string.isRequired,
    hasRoomUpgradeOffer: PropTypes.bool
  }).isRequired,
  metrics: PropTypes.shape({
    guests: PropTypes.number,
    emailsSent: PropTypes.number,
    clickThroughRate: PropTypes.number,
    identityCaptures: PropTypes.number,
    engagementRate: PropTypes.number
  }),
  preview: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  hasEmailCampaign: PropTypes.bool,
  hasDraftChanges: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onPause: PropTypes.func,
  onEnable: PropTypes.func,
  onPreviewSavibook: PropTypes.func.isRequired
}

CampaignStepCard.defaultProps = {
  metrics: EMPTY_STEP_METRICS,
  isActive: true,
  hasEmailCampaign: true,
  hasDraftChanges: false,
  onPause: undefined,
  onEnable: undefined
}

export default CampaignStepCard
